import React from 'react'

import { create } from '@bufbuild/protobuf'
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { useTranslation } from 'react-i18next'

import {
  Box,
  ChakraSpinner,
  Flex,
  TableBody,
  Cell as TableCell,
  TableColumnHeader,
  TableHeader,
  TableRoot,
  TableRow,
  TableScrollArea,
  Text,
} from '@enechain/ecloud-designsystem'
import {
  StatusLabelSchema,
  StatusLabel_Status,
} from '@enechain/eposition-proto/bfftypes/v1/label_pb'
import {
  DealPositionBadge,
  StatusBadge,
  getTextAlignLightTableStyle,
} from '@enechain/eposition-ui'

import type { ContractDealReportModel } from '../../models/contractDealReport'

interface ContractTableProps {
  data?: ContractDealReportModel[]
  loading: boolean
}

export const ContractListTable: React.FC<ContractTableProps> = ({
  data,
  loading,
}) => {
  const { t } = useTranslation(['page', 'common'])

  const columnHelper = createColumnHelper<ContractDealReportModel>()

  const columns = [
    columnHelper.accessor('dealtDate', {
      header: t('page:contract_list.table.headers.execution_date'),
      cell: (info) => info.getValue(),
      enableSorting: true,
    }),

    columnHelper.accessor('platformName', {
      header: t('page:contract_list.table.headers.execution_route'),
      cell: (info) => info.getValue(),
      enableSorting: false,
    }),

    columnHelper.accessor('eclearDealCode', {
      header: t('page:contract_list.table.headers.deal_id'),
      cell: (info) => info.getValue(),
      enableSorting: false,
    }),

    columnHelper.accessor('dealPosition', {
      header: t('page:contract_list.table.headers.side'),
      cell: (info) => {
        return <DealPositionBadge positionType={info.getValue()} />
      },
      enableSorting: false,
    }),

    columnHelper.accessor('areaCode', {
      header: t('page:contract_list.table.headers.area'),
      cell: (info) => info.getValue(),
      enableSorting: false,
    }),

    columnHelper.display({
      id: 'deliveryPeriod',
      header: t('page:contract_list.table.headers.delivery_period'),
      cell: (info) => {
        const start = info.row.original.deliveryStartDateString
        const end = info.row.original.deliveryEndDateString
        return start !== '' && end !== '' ? `${start} - ${end}` : ''
      },
      enableSorting: false,
    }),

    columnHelper.accessor('deliveryTypeString', {
      header: t('page:contract_list.table.headers.product_type'),
      cell: (info) => (
        <StatusBadge
          value={create(StatusLabelSchema, {
            status: StatusLabel_Status.NEUTRAL,
            label: info.getValue(),
          })}
        />
      ),
      enableSorting: false,
    }),

    columnHelper.accessor('deliveryHourType', {
      header: t('page:contract_list.table.headers.time_band'),
      cell: (info) => info.getValue(),
      enableSorting: false,
    }),

    columnHelper.accessor('volumeInKw', {
      header: () => (
        <Text css={{ textTransform: 'none' }}>
          {t('page:contract_list.table.headers.quantity_kw')}
        </Text>
      ),
      cell: (info) => info.getValue(),
      enableSorting: true,
      meta: {
        ...getTextAlignLightTableStyle(),
      },
    }),

    columnHelper.accessor('totalDeliveryHour', {
      header: () => (
        <Text css={{ textTransform: 'none' }}>
          {t('page:contract_list.table.headers.delivery_hours')}
        </Text>
      ),
      cell: (info) => info.getValue(),
      enableSorting: false,
      meta: {
        ...getTextAlignLightTableStyle(),
      },
    }),

    columnHelper.accessor('totalKwh', {
      header: () => (
        <Text css={{ textTransform: 'none' }}>
          {t('page:contract_list.table.headers.energy_kwh')}
        </Text>
      ),
      cell: (info) => info.getValue(),
      enableSorting: false,
      meta: {
        ...getTextAlignLightTableStyle(),
      },
    }),

    columnHelper.accessor('unitPricePerKwh', {
      header: () => (
        <Text css={{ textTransform: 'none' }}>
          {t('page:contract_list.table.headers.contract_price')}
        </Text>
      ),
      cell: (info) => info.getValue(),
      enableSorting: true,
      meta: {
        ...getTextAlignLightTableStyle(),
      },
    }),

    columnHelper.accessor('currentMarketPrice', {
      header: () => (
        <Text css={{ textTransform: 'none' }}>
          {t('page:contract_list.table.headers.current_price')}
        </Text>
      ),
      cell: (info) => info.getValue(),
      enableSorting: true,
      meta: {
        ...getTextAlignLightTableStyle(),
      },
    }),

    columnHelper.accessor('floatingIndexFormula', {
      header: t('page:contract_list.table.headers.fuel_formula'),
      cell: (info) => (info.getValue() !== '' ? info.getValue() : '-'),
      enableSorting: false,
    }),

    columnHelper.accessor('mtm', {
      header: t('page:contract_list.table.headers.mtm_amount'),
      cell: (info) => (
        <Text
          color={
            info.row.original.isPositiveMtm
              ? 'Text.highEmphasis'
              : 'Text.negativeMid'
          }
        >
          {info.getValue()}
        </Text>
      ),
      enableSorting: true,
      meta: {
        ...getTextAlignLightTableStyle(),
      },
    }),

    columnHelper.accessor('clearingFeeRate', {
      header: () => (
        <Text css={{ textTransform: 'none' }}>
          {t('page:contract_list.table.headers.clearing_fee_unit')}
        </Text>
      ),
      cell: (info) => info.getValue(),
      enableSorting: false,
      meta: {
        ...getTextAlignLightTableStyle(),
      },
    }),

    columnHelper.accessor('fee', {
      header: t('page:contract_list.table.headers.clearing_fee_total'),
      cell: (info) => info.getValue(),
      enableSorting: false,
      meta: {
        ...getTextAlignLightTableStyle(),
      },
    }),
  ]

  const table = useReactTable<ContractDealReportModel>({
    columns,
    data: data ?? [],
    getCoreRowModel: getCoreRowModel(),
  })

  return (
    <TableScrollArea>
      <TableRoot>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                const meta = header.column.columnDef.meta
                return (
                  <TableColumnHeader key={header.id} css={meta?.headerStyle}>
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}
                  </TableColumnHeader>
                )
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody position={loading ? 'relative' : 'inherit'}>
          {loading && (
            <TableRow>
              <TableCell colSpan={table.getHeaderGroups()[0].headers.length}>
                <Flex
                  position="absolute"
                  top={0}
                  right={0}
                  bottom={0}
                  left={0}
                  alignItems="center"
                  justifyContent="center"
                  opacity={0.5}
                  backgroundColor="Surface.neutralMid"
                >
                  <ChakraSpinner color="Object.highEmphasis" />
                </Flex>
              </TableCell>
            </TableRow>
          )}
          {!loading && (data == null || data.length === 0) ? (
            <TableRow>
              <TableCell colSpan={table.getHeaderGroups()[0].headers.length}>
                <Box width="100%" textAlign="center">
                  {t('page:contract_list.table.no_data')}
                </Box>
              </TableCell>
            </TableRow>
          ) : (
            table.getRowModel().rows.map((row, index) => (
              <TableRow
                key={row.id}
                backgroundColor={
                  row.original.isSettled ? 'Surface.tertiary' : 'transparent'
                }
              >
                {row.getVisibleCells().map((cell) => {
                  const meta = cell.column.columnDef.meta
                  return (
                    <TableCell
                      key={cell.id}
                      css={meta?.cellStyle}
                      isLast={table.getRowModel().rows.length - 1 === index}
                      verticalAlign="middle"
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  )
                })}
              </TableRow>
            ))
          )}
        </TableBody>
      </TableRoot>
    </TableScrollArea>
  )
}
